import { useParams } from 'react-router';
import ReferralForm from './ReferralForm/ReferralForm';
import { createIPReferral as createIPReferralRequest } from 'packages/tackle-api/src/co-sell-msft';
import { type ReferralFormFields } from './ReferralForm/referralFormSchema';
import { Button, Loader } from '@tackle-io/platform-ui';
import { useMutation } from '@tanstack/react-query';
import CreationPending from './CreationPending';
import useGoToReferralRoute from './hooks/useGoToReferralRoute/useGoToReferralRoute';
import { useCoSellContext } from '../../CoSellContextProvider';
import useCoSellSfCanvasActions from 'packages/salesforce-canvas/cosell/src/hooks/useCoSellSfCanvasActions';
import { DisplayCloudType } from '../../types/enums';
import { ReferralFormToCreateApiTransformSchema } from './ReferralForm/transformers/ReferralFormToCreateApiTransformSchema';
import { useMsftReferralMappedFields } from './hooks/useMsftReferralMappedFields/useMsftReferralMappedFields';
import { DataMapperFieldErrorsBanner } from 'packages/cosell/src/components/DataMapperBanners/DataMapperFieldErrorsBanner';
import { DataMapperLoadingErrorBanner } from 'packages/cosell/src/components/DataMapperBanners/DataMapperLoadingErrorBanner';
import { Domain } from 'pages/Settings/Account/components/FieldMapper/utils/constants';

/** temporary for development
 *  TODO: replace with error component
 */
const CreateReferralError = ({ onReset }: { onReset: () => void }) => {
  return (
    <div>
      An error occurred creating the referral. Please try again
      <Button
        onClick={onReset}
        appearance="primary"
        variant="contained"
        style={{ width: 'max-content', marginInline: 'auto' }}
        size="small"
      >
        Reset
      </Button>
    </div>
  );
};

const CreateReferralPage = () => {
  const { renderEnv } = useCoSellContext();
  const { crmId } = useParams<{ crmId: string }>();
  const { handleCoSellAction } = useCoSellSfCanvasActions();

  const mappedFieldsQuery = useMsftReferralMappedFields({
    crmId,
    enabled: !!crmId,
  });

  const mappedData = mappedFieldsQuery?.mappedData;

  const goTo = useGoToReferralRoute();
  const createIPReferral = useMutation({
    mutationFn: createIPReferralRequest,
    mutationKey: ['createIPReferral'],
  });

  const handleSubmit = async (data: ReferralFormFields) => {
    createIPReferral.mutateAsync({
      body: {
        ...ReferralFormToCreateApiTransformSchema.parse(data),
        dataMapper: {
          '$tkl-metadata': mappedData?.[`$tkl-metadata`] ?? null,
        },
      },
      tackleOperationId: 'createIPReferral',
    });
  };

  const handleCancel = () => {
    if (renderEnv === 'sf_canvas') {
      handleCoSellAction({
        id: 'go-back-to-opportunity-root',
        cloud: DisplayCloudType.MICROSOFT,
      });
      return;
    }

    /** in Tackle platform, redirect user to view referral details */
    console.info(
      'There is no where to go back to in Tackle platform, when available, handle go back to Microsoft root or referral details if editing',
    );
  };

  const handleError = (tackleId: string) => {
    // redirect user to edit form for referral to fix validation errors
    goTo.editReferral(tackleId);
  };

  const handleSuccess = (tackleId: string) => {
    if (renderEnv === 'sf_canvas') {
      handleCoSellAction({
        id: 'go-back-to-opportunity-root',
        cloud: DisplayCloudType.MICROSOFT,
      });
      return;
    }

    /** in Tackle platform, redirect user to view referral details */
    goTo.viewReferral(tackleId);
  };

  if (createIPReferral.isLoading || mappedFieldsQuery.isInitialLoading) {
    return <Loader />;
  }

  if (createIPReferral.isError) {
    return <CreateReferralError onReset={createIPReferral.reset} />;
  }

  if (!!createIPReferral.data?.tackleId) {
    return (
      <CreationPending
        onError={handleError}
        onSuccess={handleSuccess}
        requestId={createIPReferral.data.requestId}
        tackleId={createIPReferral.data.tackleId}
      />
    );
  }

  const initialValues = mappedData?.initialValues ?? null;

  const banners = (
    <>
      {!!mappedData?.fieldsWithErrors && <DataMapperFieldErrorsBanner />}
      {!!mappedFieldsQuery.isError && (
        <DataMapperLoadingErrorBanner
          domain={Domain.CO_SELL}
          isRefetching={mappedFieldsQuery.isRefetching}
          refetch={mappedFieldsQuery.refetch}
        />
      )}
    </>
  );
  return (
    /** TODO: replace wrapper with reusable page component */
    <div style={{ paddingTop: '32px' }}>
      <ReferralForm
        banners={banners}
        crmId={crmId}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </div>
  );
};

export default CreateReferralPage;
